// src/features/clients/pages/TagManagerPage.tsx
// ============================================================================
// TAG MANAGER — Administrador de Etiquetas
// ============================================================================
//
// v1.0.0 - Vista general, detalle por etiqueta, asignación masiva (reward/campaign)
// v1.1.0 - Crear etiqueta manualmente + buscar y agregar clientes individuales
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { ProLocked } from '../../../components/pro/ProLocked';
import { isPro } from '../../../utils/planPermissions';
import { Tag, Plus, X, Loader2, Users, ArrowLeft, Trash2, Gift, MessageSquare, ChevronRight, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { clientsApi } from '../../../api/clientsApi';
import { rewardsApi } from '../../../api/rewardsApi';
import { campaignsApi } from '../../../api/campaignsApi';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import SearchInput, { SearchResultItem } from '../../../components/common/SearchInput';
import { useConfirmDialog } from '../../../components/common/ConfirmDialog';

// ============================================================================
// CONSTANTES
// ============================================================================

const PAGE_SIZE = 20;

// ============================================================================
// INTERFACES
// ============================================================================

interface TagInfo {
  name: string;
  count: number;
}

interface TagClient {
  id: string;
  visitorName?: string;
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  points?: number;
  lastVisit?: string;
  tags?: string[];
}

interface RewardOption {
  id: string;
  title: string;
}

interface CampaignOption {
  id: string;
  name: string;
  totalMessagesSent: number;
}

// ============================================================================
// COMPONENTE
// ============================================================================

const TagManagerPageContent: React.FC = () => {
  const { confirm, dialog } = useConfirmDialog();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ── Estado: vista general ──
  const [tags, setTags] = useState<TagInfo[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [newTagName, setNewTagName] = useState('');

  // ── Estado: detalle de etiqueta ──
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [tagClients, setTagClients] = useState<TagClient[]>([]);
  const [tagClientsTotal, setTagClientsTotal] = useState(0);
  const [tagClientsPage, setTagClientsPage] = useState(1);
  const [tagClientsHasMore, setTagClientsHasMore] = useState(false);
  const [tagClientsLoading, setTagClientsLoading] = useState(false);

  // ── Estado: asignación masiva ──
  const [bulkMode, setBulkMode] = useState<'reward' | 'campaign' | null>(null);
  const [rewards, setRewards] = useState<RewardOption[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [selectedReward, setSelectedReward] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [bulkTagName, setBulkTagName] = useState('');
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);

  // ============================================================================
  // FETCH: Tags overview
  // ============================================================================

  const fetchTags = useCallback(async () => {
    if (!currentUser) return;
    setTagsLoading(true);
    try {
      const list = await clientsApi.tags();
      setTags(list);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('Error al cargar etiquetas');
    } finally {
      setTagsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  // ============================================================================
  // FETCH: Clients by tag
  // ============================================================================

  const fetchTagClients = useCallback(async (tag: string, page: number = 1, append: boolean = false) => {
    setTagClientsLoading(true);
    try {
      const data = await clientsApi.list({ tag, page, size: PAGE_SIZE, archived: 'exclude' });
      const items: TagClient[] = data.clients.map(c => ({
        id: c.id,
        visitorName: c.visitorName,
        phoneNumber: c.phoneNumber,
        countryCode: c.countryCode,
        email: c.email || undefined,
        points: c.points,
        lastVisit: c.lastVisit || undefined,
        tags: c.tags,
      }));
      setTagClients(prev => append ? [...prev, ...items] : items);
      setTagClientsTotal(data.total || 0);
      setTagClientsHasMore(page * PAGE_SIZE < (data.total || 0));
      setTagClientsPage(page);
    } catch (error) {
      console.error('Error fetching clients by tag:', error);
    } finally {
      setTagClientsLoading(false);
    }
  }, []);

  const handleSelectTag = useCallback((tag: string) => {
    setSelectedTag(tag);
    setTagClients([]);
    setTagClientsPage(1);
    fetchTagClients(tag, 1);
  }, [fetchTagClients]);

  const handleLoadMoreClients = useCallback(() => {
    if (selectedTag && tagClientsHasMore && !tagClientsLoading) {
      fetchTagClients(selectedTag, tagClientsPage + 1, true);
    }
  }, [selectedTag, tagClientsHasMore, tagClientsLoading, tagClientsPage, fetchTagClients]);

  // ============================================================================
  // v1.1.0: Crear etiqueta manualmente
  // ============================================================================

  const handleCreateTag = useCallback(() => {
    const tag = newTagName.trim().toLowerCase();
    if (!tag) return;

    // Verificar si ya existe
    if (tags.some(t => t.name === tag)) {
      // Si ya existe, simplemente seleccionarla
      setSelectedTag(tag);
      setTagClients([]);
      setTagClientsPage(1);
      fetchTagClients(tag, 1);
      setNewTagName('');
      return;
    }

    // Crear como nueva (virtual, se materializa al asignar primer cliente)
    setTags(prev => [...prev, { name: tag, count: 0 }]);
    setSelectedTag(tag);
    setTagClients([]);
    setTagClientsTotal(0);
    setTagClientsHasMore(false);
    setNewTagName('');
    toast.success(`Etiqueta "${tag}" creada. Agrega clientes con el buscador.`);
  }, [newTagName, tags, fetchTagClients]);

  const handleCreateTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    }
  };

  // ============================================================================
  // v1.1.0: Agregar cliente individual a la etiqueta seleccionada
  // ============================================================================

  const handleAddClientToTag = useCallback(async (client: SearchResultItem) => {
    if (!selectedTag || !currentUser) return;

    // Verificar si ya está en la lista
    if (tagClients.some(c => c.id === client.id)) {
      toast.error('Este cliente ya tiene esta etiqueta');
      return;
    }

    try {
      await clientsApi.addTag(client.id, selectedTag);

      // Agregar a la lista local
      setTagClients(prev => [{
        id: client.id,
        visitorName: client.name || undefined,
        phoneNumber: client.phone || undefined,
      }, ...prev]);
      setTagClientsTotal(prev => prev + 1);

      // Actualizar conteo en la lista general
      setTags(prev => prev.map(t =>
        t.name === selectedTag ? { ...t, count: t.count + 1 } : t
      ));

      toast.success(`${client.name || 'Cliente'} etiquetado como "${selectedTag}"`);
    } catch (error) {
      console.error('Error adding client to tag:', error);
      toast.error('Error al etiquetar cliente');
    }
  }, [selectedTag, currentUser, tagClients]);

  // ============================================================================
  // Remove tag from one client
  // ============================================================================

  const handleRemoveTagFromClient = useCallback(async (clientId: string, tag: string) => {
    if (!currentUser) return;
    try {
      await clientsApi.removeTag(clientId, tag);
      setTagClients(prev => prev.filter(c => c.id !== clientId));
      setTagClientsTotal(prev => Math.max(0, prev - 1));
      setTags(prev => prev.map(t =>
        t.name === tag ? { ...t, count: Math.max(0, t.count - 1) } : t
      ).filter(t => t.count > 0 || t.name === selectedTag));
      toast.success('Etiqueta removida');
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Error al remover etiqueta');
    }
  }, [currentUser, selectedTag]);

  // ============================================================================
  // Delete tag from ALL clients (batch)
  // ============================================================================

  const handleDeleteTagFromAll = useCallback(async (tag: string) => {
    if (!currentUser) return;
    const ok = await confirm({
      title: `¿Eliminar la etiqueta "${tag}"?`,
      message: 'Se quita de todos los clientes que la tengan. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar etiqueta',
    });
    if (!ok) return;

    setBulkExecuting(true);
    try {
      // Una sola llamada server-side borra la etiqueta de TODOS los contactos.
      const r = await clientsApi.deleteTagFromAll(tag);
      setTags(prev => prev.filter(t => t.name !== tag));
      if (selectedTag === tag) { setSelectedTag(null); setTagClients([]); }
      toast.success(`Etiqueta "${tag}" eliminada de ${r.affected || 0} clientes`);
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Error al eliminar etiqueta');
    } finally {
      setBulkExecuting(false);
      setBulkProgress(null);
    }
  }, [currentUser, selectedTag]);

  // ============================================================================
  // BULK ASSIGN: Fetch sources
  // ============================================================================

  const handleOpenBulkMode = useCallback(async (mode: 'reward' | 'campaign') => {
    setBulkMode(mode);
    setSelectedReward('');
    setSelectedCampaign('');
    setBulkTagName('');

    if (mode === 'reward' && rewards.length === 0) {
      setRewardsLoading(true);
      try {
        const list = await rewardsApi.list();
        setRewards(list.map(r => ({ id: r.id, title: r.title })));
      } catch (error) {
        console.error('Error fetching rewards:', error);
        toast.error('Error al cargar recompensas');
      } finally {
        setRewardsLoading(false);
      }
    }

    if (mode === 'campaign' && campaigns.length === 0) {
      setCampaignsLoading(true);
      try {
        const list = await campaignsApi.list();
        setCampaigns(list.map(c => ({
          id: c.id,
          name: c.name || 'Sin nombre',
          totalMessagesSent: c.totalMessagesSent || 0,
        })));
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      } finally {
        setCampaignsLoading(false);
      }
    }
  }, [rewards.length, campaigns.length]);

  // ============================================================================
  // BULK ASSIGN: Execute
  // ============================================================================

  const handleBulkAssign = useCallback(async () => {
    const tag = bulkTagName.trim().toLowerCase();
    if (!tag || !currentUser) return;

    setBulkExecuting(true);
    let clientIds: string[] = [];

    try {
      // 1) Resolver la fuente de contactos (ids = contact_id de base).
      if (bulkMode === 'reward' && selectedReward) {
        clientIds = await rewardsApi.redemptionContactIds(selectedReward);
      } else if (bulkMode === 'campaign' && selectedCampaign) {
        const queue = await campaignsApi.queue(selectedCampaign, 'sent');
        clientIds = [...new Set(queue.map(q => String(q.contact_id)))];
      }

      if (clientIds.length === 0) {
        toast.error('No se encontraron clientes para etiquetar');
        setBulkExecuting(false);
        return;
      }

      // 2) Una sola llamada server-side etiqueta a TODOS los contactos.
      setBulkProgress({ current: 0, total: clientIds.length });
      const r = await clientsApi.bulkTag(tag, clientIds, 'add');
      setBulkProgress({ current: clientIds.length, total: clientIds.length });

      toast.success(`Etiqueta "${tag}" asignada a ${r.affected ?? clientIds.length} clientes`);
      setBulkMode(null);
      setBulkTagName('');
      setSelectedReward('');
      setSelectedCampaign('');
      setTimeout(() => fetchTags(), 3000);
    } catch (error) {
      console.error('Error bulk assigning tag:', error);
      toast.error('Error en asignación masiva');
    } finally {
      setBulkExecuting(false);
      setBulkProgress(null);
    }
  }, [bulkMode, bulkTagName, selectedReward, selectedCampaign, currentUser, fetchTags]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Layout>
      <div className="space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-content-3 hover:text-content-2 mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center mt-1">
              <Tag className="h-6 w-6 mr-2 text-purple-600 dark:text-purple-400" /> Administrar Etiquetas
            </h1>
            <p className="text-sm text-content-3 mt-1">
              Organiza tus clientes con etiquetas para segmentar campañas
            </p>
          </div>
          <button
            onClick={fetchTags}
            disabled={tagsLoading}
            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${tagsLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* ── Vista general de etiquetas ── */}
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-line">
            <h3 className="text-lg font-medium text-content flex items-center">
              Etiquetas del negocio
              {!tagsLoading && (
                <span className="ml-2 text-sm font-normal text-content-3">
                  ({tags.length} {tags.length === 1 ? 'etiqueta' : 'etiquetas'})
                </span>
              )}
            </h3>
          </div>

          <div className="px-4 py-4 sm:px-6 space-y-4">

            {/* v1.1.0: Crear etiqueta nueva */}
            <div>
              <label className="block text-sm font-medium text-content-2 mb-1">
                Crear nueva etiqueta
              </label>
              <div className="flex gap-2 max-w-md">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={handleCreateTagKeyDown}
                  placeholder="Ej: vip, promo-verano, corte..."
                  maxLength={30}
                  className="flex-1 border border-line rounded-md px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Crear
                </button>
              </div>
            </div>

            {/* Lista de etiquetas existentes */}
            {tagsLoading ? (
              <div className="text-center py-6">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-purple-500" />
                <p className="text-sm text-content-3">Cargando etiquetas...</p>
              </div>
            ) : tags.length === 0 ? (
              <div className="text-center py-6">
                <Tag className="h-10 w-10 mx-auto mb-3 text-content-3" />
                <p className="text-sm text-content-3">No hay etiquetas aún</p>
                <p className="text-xs text-content-3 mt-1">
                  Crea una arriba para empezar
                </p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <button
                    key={tag.name}
                    onClick={() => handleSelectTag(tag.name)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full border transition-colors group ${
                      selectedTag === tag.name
                        ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-300 ring-2 ring-purple-300'
                        : 'bg-surface text-content-2 border-line hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10'
                    }`}
                  >
                    <Tag className="h-3 w-3 mr-1.5 flex-shrink-0" />
                    {tag.name}
                    <span className={`ml-1.5 text-xs ${selectedTag === tag.name ? 'text-purple-500' : 'text-content-3'}`}>
                      {tag.count}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteTagFromAll(tag.name); }}
                      disabled={bulkExecuting}
                      className="hover-reveal ml-1.5 opacity-0 group-hover:opacity-100 text-content-3 hover:text-red-500 transition-opacity disabled:opacity-50"
                      title={`Eliminar "${tag.name}" de todos los clientes`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Detalle de etiqueta seleccionada ── */}
        {selectedTag && (
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
            <div className="px-4 py-5 sm:px-6 border-b border-line flex items-center justify-between">
              <h3 className="text-lg font-medium text-content flex items-center">
                <Tag className="h-5 w-5 mr-2 text-purple-500" />
                "{selectedTag}"
                <span className="ml-2 text-sm font-normal text-content-3">
                  ({tagClientsTotal} {tagClientsTotal === 1 ? 'cliente' : 'clientes'})
                </span>
              </h3>
              <button
                onClick={() => { setSelectedTag(null); setTagClients([]); }}
                className="text-content-3 hover:text-content-2 p-1 rounded-full hover:bg-canvas"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* v1.1.0: Buscador para agregar clientes a esta etiqueta */}
            <div className="px-4 py-3 sm:px-6 bg-purple-50 dark:bg-purple-500/10 border-b border-purple-100">
              <label className="block text-sm font-medium text-purple-800 dark:text-purple-300 mb-1.5 flex items-center">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Agregar cliente a "{selectedTag}"
              </label>
              <SearchInput
                onSearch={() => {}}
                onClientSelect={handleAddClientToTag}
                placeholder="Buscar por nombre o teléfono..."
                showResults={true}
                preventClientNavigation={true}
                debounceTime={400}
                minSearchLength={2}
                className="w-full"
              />
            </div>

            {/* Lista de clientes con esta etiqueta */}
            <div className="divide-y divide-line">
              {tagClientsLoading && tagClients.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-purple-500" />
                  <p className="text-sm text-content-3">Cargando clientes...</p>
                </div>
              ) : tagClients.length === 0 && !tagClientsLoading ? (
                <div className="px-4 py-6 text-center text-sm text-content-3">
                  <Users className="h-8 w-8 mx-auto mb-2 text-content-3" />
                  Aún no hay clientes con esta etiqueta.
                  <br />
                  <span className="text-xs text-content-3">Usa el buscador de arriba para agregar clientes.</span>
                </div>
              ) : (
                tagClients.map(client => (
                  <div key={client.id} className="px-4 py-3 flex items-center justify-between hover:bg-surface-2 transition-colors">
                    <div className="flex items-center min-w-0 gap-3">
                      <ClientContactInfo
                        clientId={client.id}
                        clientName={client.visitorName || null}
                        phoneNumber={client.phoneNumber || ''}
                        countryCode={client.countryCode || ''}
                        email={client.email || null}
                        showName={true}
                        nameAsLink={true}
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveTagFromClient(client.id, selectedTag)}
                      className="ml-3 p-1.5 text-content-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors flex-shrink-0"
                      title="Quitar etiqueta de este cliente"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {tagClientsHasMore && !tagClientsLoading && (
              <div className="px-4 py-3 bg-surface-2 text-center border-t border-line">
                <button onClick={handleLoadMoreClients} className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:text-purple-300">
                  Cargar más clientes...
                </button>
              </div>
            )}

            {tagClientsLoading && tagClients.length > 0 && (
              <div className="px-4 py-3 bg-surface-2 text-center border-t border-line">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2 text-purple-500" />
                <span className="text-sm text-content-3">Cargando...</span>
              </div>
            )}
          </div>
        )}

        {/* ── Asignación masiva ── */}
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-line">
            <h3 className="text-lg font-medium text-content flex items-center">
              <Users className="h-5 w-5 mr-2 text-content-3" />
              Asignación Masiva
            </h3>
            <p className="mt-1 text-sm text-content-3">
              Etiqueta múltiples clientes de una vez según un criterio
            </p>
          </div>

          <div className="px-4 py-4 sm:px-6 space-y-4">

            {/* Selector de modo */}
            {!bulkMode && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => handleOpenBulkMode('reward')}
                  className="flex items-center p-4 border border-line rounded-lg hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-all text-left group"
                >
                  <div className="h-10 w-10 bg-pink-100 dark:bg-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mr-3 group-hover:bg-pink-200 dark:group-hover:bg-pink-500/30 transition-colors">
                    <Gift className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div>
                    <p className="font-medium text-content">Por recompensa canjeada</p>
                    <p className="text-xs text-content-3 mt-0.5">Etiqueta clientes que canjearon una recompensa específica</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-content-3 ml-auto flex-shrink-0" />
                </button>

                <button
                  onClick={() => handleOpenBulkMode('campaign')}
                  className="flex items-center p-4 border border-line rounded-lg hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-all text-left group"
                >
                  <div className="h-10 w-10 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mr-3 group-hover:bg-blue-200 transition-colors">
                    <MessageSquare className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-medium text-content">Por campaña anterior</p>
                    <p className="text-xs text-content-3 mt-0.5">Etiqueta clientes a quienes se les envió una campaña</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-content-3 ml-auto flex-shrink-0" />
                </button>
              </div>
            )}

            {/* Formulario de asignación masiva */}
            {bulkMode && (
              <div className="bg-purple-50 dark:bg-purple-500/10 rounded-lg border border-purple-200 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300 flex items-center">
                    {bulkMode === 'reward' ? (
                      <><Gift className="h-4 w-4 mr-1.5" /> Etiquetar por recompensa</>
                    ) : (
                      <><MessageSquare className="h-4 w-4 mr-1.5" /> Etiquetar por campaña</>
                    )}
                  </h4>
                  <button
                    onClick={() => setBulkMode(null)}
                    className="text-content-3 hover:text-content-2 p-1 rounded-full hover:bg-surface transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Paso 1: Seleccionar fuente */}
                {bulkMode === 'reward' && (
                  <div>
                    <label className="block text-xs font-medium text-content-2 mb-1">
                      Recompensa canjeada
                    </label>
                    {rewardsLoading ? (
                      <div className="flex items-center gap-2 text-xs text-content-3 py-2">
                        <Loader2 className="h-3 w-3 animate-spin" /> Cargando recompensas...
                      </div>
                    ) : (
                      <select
                        value={selectedReward}
                        onChange={(e) => setSelectedReward(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-sm border-line focus:ring-purple-500 focus:border-purple-500 rounded-md"
                      >
                        <option value="">Seleccionar recompensa...</option>
                        {rewards.map(r => (
                          <option key={r.id} value={r.id}>
                            {r.title}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {bulkMode === 'campaign' && (
                  <div>
                    <label className="block text-xs font-medium text-content-2 mb-1">
                      Campaña
                    </label>
                    {campaignsLoading ? (
                      <div className="flex items-center gap-2 text-xs text-content-3 py-2">
                        <Loader2 className="h-3 w-3 animate-spin" /> Cargando campañas...
                      </div>
                    ) : (
                      <select
                        value={selectedCampaign}
                        onChange={(e) => setSelectedCampaign(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-sm border-line focus:ring-purple-500 focus:border-purple-500 rounded-md"
                      >
                        <option value="">Seleccionar campaña...</option>
                        {campaigns.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.totalMessagesSent} enviados)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Paso 2: Nombre de la etiqueta */}
                <div>
                  <label className="block text-xs font-medium text-content-2 mb-1">
                    Etiqueta a asignar
                  </label>
                  <input
                    type="text"
                    value={bulkTagName}
                    onChange={(e) => setBulkTagName(e.target.value)}
                    placeholder="Ej: vip, promo-verano, corte..."
                    maxLength={30}
                    className="block w-full border border-line rounded-md px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                  />
                  {tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-[10px] text-content-3 mr-1 self-center">Existentes:</span>
                      {tags.slice(0, 10).map(t => (
                        <button
                          key={t.name}
                          type="button"
                          onClick={() => setBulkTagName(t.name)}
                          className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                            bulkTagName === t.name
                              ? 'bg-purple-100 dark:bg-purple-500/20 border-purple-300 text-purple-700 dark:text-purple-300'
                              : 'bg-surface border-line text-content-3 hover:border-purple-200'
                          }`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Barra de progreso */}
                {bulkProgress && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-purple-600 dark:text-purple-400">
                      <span>Etiquetando clientes...</span>
                      <span>{bulkProgress.current}/{bulkProgress.total}</span>
                    </div>
                    <div className="w-full bg-purple-100 dark:bg-purple-500/20 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-[width] duration-300"
                        style={{ width: `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Botón ejecutar */}
                <button
                  onClick={handleBulkAssign}
                  disabled={
                    bulkExecuting ||
                    !bulkTagName.trim() ||
                    (bulkMode === 'reward' && !selectedReward) ||
                    (bulkMode === 'campaign' && !selectedCampaign)
                  }
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {bulkExecuting ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Procesando...</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" /> Asignar etiqueta</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Nota informativa ── */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">¿Cómo funcionan las etiquetas?</p>
            <p className="text-amber-700 mt-1">
              Las etiquetas se guardan en cada cliente y se sincronizan automáticamente.
              Los cambios pueden tardar unos segundos en reflejarse en los conteos.
              También puedes etiquetar clientes desde su historial individual y usarlas como filtro en campañas personalizadas.
            </p>
          </div>
        </div>

      </div>
      {dialog}
    </Layout>
  );
};

// Bloqueo PRO (2-oct-2026, Carlos: «etiquetas igual bloqueo total»; plan pantallas PRO F4). Las etiquetas
// sirven para segmentar campañas, que son PRO. Los 3 gratis que las tenían eran pruebas sueltas.
const TagManagerPage: React.FC = () => {
  const { currentUser } = useAuth();
  if (isPro(currentUser?.subscription)) return <TagManagerPageContent />;
  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <Tag className="h-6 w-6 mr-2 text-purple-600 dark:text-purple-400" /> Administrar Etiquetas
          </h1>
          <p className="text-sm text-content-3 mt-1">Organiza tus clientes con etiquetas para segmentar campañas</p>
        </div>
        <ProLocked feature="tags" />
      </div>
    </Layout>
  );
};

export default TagManagerPage;